import { useState, type FormEvent, type MouseEvent } from 'react';
import toast from 'react-hot-toast';

import { type VoiceResponse } from 'features/generation-lab';
import { getApiErrorMessage } from 'utils/apiErrors';

import {
  type TutorConfiguration,
  type TutorConfigurationBody,
  useCreateTutorConfiguration,
  useReplaceTutorConfiguration,
} from '../services/tutorConfigurationsApi';

interface TutorConfigurationDialogProps {
  configuration: TutorConfiguration | null;
  languages: Array<{
    code: string;
    name: string;
    supportedAsNative: boolean;
    supportedAsTarget: boolean;
  }>;
  voices: VoiceResponse[];
  onClose: () => void;
}

function voiceLabel(voice: VoiceResponse): string {
  const metadata = [
    voice.providerName,
    voice.languageCodes.join(', '),
    voice.localeCode,
    voice.isActive ? null : 'inactive',
  ]
    .filter(Boolean)
    .join(' · ');
  return `${voice.name} · ID ${voice.id}${metadata ? ` · ${metadata}` : ''}`;
}

export function TutorConfigurationDialog({
  configuration,
  languages,
  voices,
  onClose,
}: TutorConfigurationDialogProps) {
  const createMutation = useCreateTutorConfiguration();
  const replaceMutation = useReplaceTutorConfiguration();
  const [nativeLanguageCode, setNativeLanguageCode] = useState(
    configuration?.nativeLanguageCode ?? ''
  );
  const [targetLanguageCode, setTargetLanguageCode] = useState(
    configuration?.targetLanguageCode ?? ''
  );
  const [voiceId, setVoiceId] = useState(configuration ? String(configuration.voiceId) : '');
  const [explainerVoiceId, setExplainerVoiceId] = useState(
    configuration ? String(configuration.explainerVoiceId) : ''
  );
  const [formError, setFormError] = useState<string | null>(null);

  const nativeLanguages = languages.filter(
    (language) => language.supportedAsNative || language.code === configuration?.nativeLanguageCode
  );
  const targetLanguages = languages.filter(
    (language) => language.supportedAsTarget || language.code === configuration?.targetLanguageCode
  );
  const tutorVoices = voices
    .filter(
      (voice) =>
        (voice.isActive && voice.languageCodes.includes(targetLanguageCode)) ||
        (voice.id === configuration?.voiceId &&
          targetLanguageCode === configuration.targetLanguageCode)
    )
    .sort((left, right) => left.name.localeCompare(right.name) || left.id - right.id);
  const explainerVoices = voices
    .filter(
      (voice) =>
        (voice.isActive && voice.languageCodes.includes(nativeLanguageCode)) ||
        (voice.id === configuration?.explainerVoiceId &&
          nativeLanguageCode === configuration.nativeLanguageCode)
    )
    .sort((left, right) => left.name.localeCompare(right.name) || left.id - right.id);
  const isPending = createMutation.isPending || replaceMutation.isPending;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    if (!nativeLanguageCode || !targetLanguageCode || !voiceId || !explainerVoiceId) {
      setFormError(
        'Native language, target language, Tutor voice, and Explainer voice are required.'
      );
      return;
    }

    const body: TutorConfigurationBody = {
      nativeLanguageCode,
      targetLanguageCode,
      voiceId: Number(voiceId),
      explainerVoiceId: Number(explainerVoiceId),
    };

    try {
      if (configuration) {
        await replaceMutation.mutateAsync({ id: configuration.id, body });
        toast.success('Tutor configuration updated');
      } else {
        await createMutation.mutateAsync(body);
        toast.success('Tutor configuration created');
      }
      onClose();
    } catch (error) {
      const message = getApiErrorMessage(error);
      setFormError(message);
      toast.error(message);
    }
  };

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && !isPending) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutor-configuration-dialog-title"
      onClick={handleBackdropClick}
    >
      <div className="max-h-[calc(100vh-2rem)] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 id="tutor-configuration-dialog-title" className="text-xl font-semibold text-gray-900">
            {configuration ? 'Edit Tutor configuration' : 'Add Tutor configuration'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            x
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-5">
          {formError && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {formError}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor="tutor-native-language"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Native language
              </label>
              <select
                id="tutor-native-language"
                value={nativeLanguageCode}
                onChange={(event) => {
                  setNativeLanguageCode(event.target.value);
                  setExplainerVoiceId('');
                }}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select native language</option>
                {nativeLanguages.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.name} ({language.code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="tutor-target-language"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Target language
              </label>
              <select
                id="tutor-target-language"
                value={targetLanguageCode}
                onChange={(event) => {
                  setTargetLanguageCode(event.target.value);
                  setVoiceId('');
                }}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select target language</option>
                {targetLanguages.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.name} ({language.code})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="tutor-voice" className="mb-1 block text-sm font-medium text-gray-700">
              Tutor voice
            </label>
            <select
              id="tutor-voice"
              value={voiceId}
              onChange={(event) => setVoiceId(event.target.value)}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Tutor voice</option>
              {tutorVoices.map((voice) => (
                <option key={voice.id} value={voice.id}>
                  {voiceLabel(voice)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="explainer-voice"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Explainer voice
            </label>
            <select
              id="explainer-voice"
              value={explainerVoiceId}
              onChange={(event) => setExplainerVoiceId(event.target.value)}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Explainer voice</option>
              {explainerVoices.map((voice) => (
                <option key={voice.id} value={voice.id}>
                  {voiceLabel(voice)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isPending ? 'Saving...' : configuration ? 'Save changes' : 'Create configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
